/**
 * Fit-score color tier resolution for the candidate card.
 *
 * As of ranking.system v3 / rank_one.system v3, `fit_score` is a STORED
 * value on the `rankings` table, emitted by the AI and persisted at rank
 * time. The card displays `rankings.fit_score` directly; this module
 * resolves color tiers and provides the categorical null-fallback for
 * legacy rows.
 *
 * The old position-bonus formula (fit_base + positionPct * 28) has been
 * retired. Deleting a candidate no longer changes any other candidate's
 * score — the % changes ONLY on a re-rank.
 *
 * Used by:
 *   - 30b Dashboard (`RankedCandidateCard`)
 *   - 30c Candidates table (rank + fit cell)
 *   - 30d Candidate page header (banner score)
 *
 * Color tiers (recovered from prototype `scoreColor()`):
 *   - >= 78  → `positive` (#00C896, brand-positive — hot)
 *   - >= 62  → `blue`     (#3B82F6, blue-500    — warm)
 *   - <  62  → `warn`     (#F59E0B, brand-warn  — lukewarm)
 *
 * Three tiers only — the prototype caps the floor at amber because the
 * fit-overall=`weak` baseline is ~30%. **Do not introduce a fourth red
 * tier.** Family rule (web-designer §3 ratification).
 */

import { FIT_COLOR_POSITIVE, FIT_COLOR_BLUE, FIT_COLOR_WARN } from "./tokens";

export type FitOverall = "strong" | "moderate" | "weak";

type FitColorTier = "positive" | "blue" | "warn";

/**
 * Categorical fallback: returns the placeholder fit_score for a given
 * fit_overall bucket.
 *
 * Used when a ranking row's `fit_score` is null (legacy rows post-backfill
 * should not be null, but be defensive). Values match the migration backfill
 * (0067_rankings_fit_score) and `_FIT_SCORE_DEFAULTS` in ranking.py /
 * rank_one.py.
 *
 * @param fitOverall  AI-emitted fit assessment from rankings.fit_overall.
 * @returns           Integer placeholder score (75 / 55 / 35 / 55 for unknown).
 */
export function fitScoreFromOverall(fitOverall: FitOverall): number {
  const defaults: Record<FitOverall, number> = {
    strong: 75,
    moderate: 55,
    weak: 35,
  };
  return defaults[fitOverall] ?? 55;
}

/**
 * Canonical fit LABEL for a `fit_overall` enum value — the single source of
 * truth for the headline-fit text across every surface (Dashboard
 * `RankedCandidateCard`, All-Candidates `CandidatesTableRow`, and anywhere
 * else a fit label renders).
 *
 * D5: the "… Fit" suffix is the dominant pattern (the Dashboard card has
 * always rendered "Strong Fit"/"Moderate Fit"/"Weak Fit"). The All-Candidates
 * table previously rendered the bare word ("Moderate"), which made the same
 * candidate look like two different things on two screens. Routing both
 * through this helper makes the text identical wherever it appears.
 */
export function fitLabel(fitOverall: FitOverall): string {
  switch (fitOverall) {
    case "strong":
      return "Strong Fit";
    case "moderate":
      return "Moderate Fit";
    case "weak":
      return "Weak Fit";
  }
}

/**
 * Resolve a fit-color tier from a 0–100 score.
 *
 * The tier name maps to the brand token used by the card's tinted band,
 * top-border, and fit-score label color (see web-designer §3).
 */
export function fitScoreColor(score: number): FitColorTier {
  if (score >= 78) return "positive";
  if (score >= 62) return "blue";
  return "warn";
}

/**
 * Hex value for a fit color tier — used as inline `style` on the card's
 * cc-band tint (`${hex}14` for 8% opacity), top-border, and rank-badge fill.
 *
 * Slate fallback (#475569) for terminal-state cards (opted_out + rejected)
 * is applied at the card level, not here — tier-color logic is positive/
 * warm/lukewarm only.
 */
export function fitColorHex(tier: FitColorTier): string {
  switch (tier) {
    case "positive":
      return FIT_COLOR_POSITIVE;
    case "blue":
      return FIT_COLOR_BLUE;
    case "warn":
      return FIT_COLOR_WARN;
  }
}
